import {
  ControlBarProps,
  useMaybeLayoutContext,
  useLocalParticipantPermissions,
  usePersistentUserChoices,
  ChatToggle,
  ChatIcon,
  DisconnectButton,
  LeaveIcon,
  useRoomContext,
  useTracks,
  useDataChannel,
} from "@livekit/components-react";
import { TrackToggle } from ".";
import { useMediaQuery } from "@mui/material";
import { createLocalAudioTrack, createLocalVideoTrack, DataPacket_Kind, LocalParticipant, Track } from "livekit-client";
import React, {useEffect, useRef, useState } from "react";
import { ScreenShare as ScreenShareElectron } from "./ScreenShare";
import { ReceivedDataMessage } from "@livekit/components-core";
import RemoteControl from "../rdp/RemoteControl";
import { RemoteAllow } from "../rdp/remoteAllow";
import { useNavigate } from "react-router-dom";
import { getSessionRecordingId, setSessionRerdingId } from "../../services/storage.service";
import { useRecoilState, useRecoilValue } from "recoil";
import {showHeaderState, staticConfigAtom } from "../../atom/atom";
import { MediaDeviceMenu } from "./MediaDeviceMenu";
import shareScreenIcon from "../../assets/share-screen.png";
import sessionRecordIcon from "../../assets/session-record-icon.png";
import useSnackbar from "../../utils/useSnackbar";
import { SessionRecordService } from "../../services/sessinRecord.service";
import { TokenPayload } from "../../models/IToken";
import TokenService from "../../services/token.service";
import sessionRecordRedIcon from '../../assets/session-record-start.png'
import LeaveButtonWithDropdown from "../leave-btn-dropdown/LeaveButtonWithDropdown";
import TerninateService from "../../services/terminate.service";
import { useMediaShortcuts } from "../../utils/useMediaShortcuts";

export function ControlBar({
  variation,
  controls,
  saveUserChoices = true,
  ...props
}: ControlBarProps) {

  const sessionRecordingService = new SessionRecordService

  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();
  const isFeatureConfigured = useRecoilValue(staticConfigAtom);
  const micWrapperRef = useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);
  const isTooLittleSpace = useMediaQuery(
    `(max-width: ${isChatOpen ? 1000 : 760}px)`
  );

  const defaultVariation = isTooLittleSpace ? "minimal" : "verbose";
  variation ??= defaultVariation;

  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
  } else {
    visibleControls.camera ??= localPermissions.canPublish;
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.screenShare ??= localPermissions.canPublish;
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const showIcon = React.useMemo(
    () => variation === "minimal" || variation === "verbose",
    [variation]
  );
  const showText = React.useMemo(
    () => variation === "textOnly" || variation === "verbose",
    [variation]
  );

  const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);

  const onScreenShareChange = React.useCallback(
    (enabled: boolean) => {
      setIsScreenShareEnabled(enabled);
    },
    [setIsScreenShareEnabled]
  );

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });
  const room = useRoomContext();


  const microphoneOnChange = React.useCallback(
    async (enabled: boolean) => {
      const trackPublication = room.localParticipant.getTrack(Track.Source.Microphone);
      saveAudioInputEnabled(enabled);
  
      if (!enabled && trackPublication?.track) {
        room.localParticipant.unpublishTrack(trackPublication.track);
      } else if (enabled && !trackPublication?.track) {
        const micTrack = await createLocalAudioTrack();
        await room.localParticipant.publishTrack(micTrack, { source: Track.Source.Microphone });
      }
    },
    [room.localParticipant, saveAudioInputEnabled]
  );

  const microphoneEnableClick = React.useCallback(
    async (enabled: boolean) => {
      console.log("Microphone toggle via shortcut", enabled);
      if (micWrapperRef.current) {
        const btn = micWrapperRef.current.querySelector('button');
        if (btn) {
          (btn as HTMLButtonElement).click();
        }
      }
    },
    [room.localParticipant, saveAudioInputEnabled]
  )
  
  const cameraOnChange = React.useCallback(
    async (enabled: boolean) => {
      const trackPublication = room.localParticipant.getTrack(Track.Source.Camera);
      saveVideoInputEnabled(enabled);
  
      if (!enabled && trackPublication?.track) {
        room.localParticipant.unpublishTrack(trackPublication.track);
      } else if (enabled && !trackPublication?.track) {
        const videoTrack = await createLocalVideoTrack();
        await room.localParticipant.publishTrack(videoTrack, { source: Track.Source.Camera });
      }
    },
    [room.localParticipant, saveVideoInputEnabled]
  );

  const [screenShareAvl, setScreenShareAvl] = React.useState(true);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleSnackbarNoticationEvent = (message: string) => {
    showSnackbar({
      message: message,
      severity: "info",
    });
  };

  useMediaShortcuts({
    room,
    toggleMic: microphoneEnableClick,
    toggleCamera: cameraOnChange,
    notify: handleSnackbarNoticationEvent
  });

  const  handleScreeShareNoticationEvent = () => {
    handleSnackbarNoticationEvent('Share screen is disabled when remote session is going');
  }

  // start of session recording feature

  const [sessionRecordingLabel, setSessionRecordingLabel] = useState('Start recording');
  const [sessionRecordingIcon, setSessionRecordingIcon] = useState(sessionRecordIcon);
  const [adminLeaveOption, setAdminLeaveOption] = useState(false);
  const [sessionRecordingDisabled, setSessionRecordingDisabled] = useState(false);
  const { message, send } = useDataChannel("session-record-channel", (msg) => sessionRecordingNotificationAction(msg));
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  const handleSessionRecording = async () => {
    let sessionId = getSessionId();

   if(!getSessionRecordingId()){
    handleStartSessionRecording(sessionId);
   }else{
    handleStopSessionRecording(sessionId);
   }
  }

  const handleStartSessionRecording = async(sessionId: string) => {
    try {
      setSessionRecordingDisabled(true);
      
      await sessionRecordingService.startRecording(sessionId);

      setSessionRecordingDisabled(false);
      handleSnackbarNoticationEvent('Session recording started by you');
      setSessionRecordingLabel('Stop recording');
      setSessionRecordingIcon(sessionRecordRedIcon); 
      sendSessionRecordingNotification('session-recording-start', getSessionRecordingId(), room.localParticipant?.name);

    } catch (error) {
      setSessionRecordingDisabled(true);
      handleSnackbarNoticationEvent('Something went wrong');
      console.error('Failed to start recording:', error);
      showSnackbar({message: 'Enable to start recording.',severity: "warning"});
      setSessionRecordingDisabled(false);
      setSessionRecordingLabel('Start recording');
      setSessionRecordingIcon(sessionRecordIcon); 

    }
  }

  const handleStopSessionRecording = async(sessionId: string) => {
    try {
      setSessionRecordingDisabled(true);
      await sessionRecordingService.stopRecording(sessionId);

      setSessionRecordingDisabled(false);
      setSessionRecordingLabel('Start recording');
      setSessionRecordingIcon(sessionRecordIcon); 
      sendSessionRecordingNotification('session-recording-stop');
      handleSnackbarNoticationEvent('Session recording ended');

    } catch (error) {
      setSessionRecordingDisabled(true);
      console.error('Failed to start recording:', error);
      handleSnackbarNoticationEvent('Something went wrong');
    }
  }

  const sessionRecordingNotificationAction = (message: ReceivedDataMessage<"session-record-channel">) => {
    if (!message) return;
    const msg = decoder.decode(message.payload);
    let _msg = JSON.parse(msg);

    if(_msg.message === 'session-recording-start'){
      
      setSessionRerdingId(_msg.recordingId);
      setSessionRecordingLabel('Stop recording');
      setSessionRecordingIcon(sessionRecordRedIcon);
      handleSnackbarNoticationEvent('Session recording started by ' + _msg.startedBy);

    }else if(_msg.message === 'session-recording-stop'){
      
      setSessionRecordingLabel('Start recording');
      setSessionRecordingIcon(sessionRecordIcon);
      handleSnackbarNoticationEvent('Session recording ended');
    }
  }


  const sendSessionRecordingNotification = (messageContent: string, sessionRecordingId?: string | null, participantName?: string | null) => {

    const sessionRecordingInfo = JSON.stringify({ message: messageContent, recordingId: sessionRecordingId, startedBy: participantName });
    send(encoder.encode(sessionRecordingInfo), {
    kind: DataPacket_Kind.RELIABLE,
  });

  }

  const getSessionId = () => {
    let parsedToken: TokenPayload = TokenService.parseToken(true, "");
    return parsedToken.video.room;
  };
  
  const tracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    {}
  );
  React.useEffect(() => {
    let metadata;
    try {
      metadata = JSON.parse(tracks[0]?.participant?.metadata ?? "");
    } catch {
      metadata = {};
    }
    if (!!metadata.remoteAccessLock && !(tracks[0]?.participant instanceof LocalParticipant)) setScreenShareAvl(false);
    else if (!screenShareAvl) setScreenShareAvl(true);
  }, [tracks]);

  useEffect(() => {
    let parsedToken: TokenPayload = TokenService.parseToken(true, "");
    if(parsedToken.video.roomAdmin){
      setAdminLeaveOption(true);
    }
  })
  

  const navigate = useNavigate();
  const [showHeader, setShowHeader] = useRecoilState(showHeaderState);

  const checkToStopRecording = async () => {

    const recordingId = getSessionRecordingId();
    if(!!recordingId && !room.participants.size){
      await handleSessionRecording();
    }
  }

  const onLeaveSession = async () => {
    await checkToStopRecording();
    setShowHeader(!showHeader);
    navigate("/feedback");
  };

  const onEndForAll = async () => {

    await checkToStopRecording();
    await TerninateService.terminateSession();
    navigate('/feedback');
  };

  return (
    <>
      {SnackbarComponent}
      <div className="controlBar-container">
        <div className="lk-button-group">
          <RemoteControl />
        </div>
        {!!(window as any).electronAPI && RemoteAllow()}
        {visibleControls.microphone && (
          <div className="lk-button-group" ref={micWrapperRef}>
            <TrackToggle
              source={Track.Source.Microphone}
              showIcon={showIcon}
              onChange={microphoneOnChange}
            >
              {showText && "Microphone"}
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                kind="audioinput"
                onActiveDeviceChange={(_kind, deviceId) =>
                  saveAudioInputDeviceId(deviceId ?? "")
                }
              />
            </div>
          </div>
        )}
        {visibleControls.camera && (
          <div className="lk-button-group">
            <TrackToggle
              source={Track.Source.Camera}
              showIcon={showIcon}
              onChange={cameraOnChange}
            >
              {showText && "Camera"}
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                kind="videoinput"
                onActiveDeviceChange={(_kind, deviceId) =>
                  saveVideoInputDeviceId(deviceId ?? "")
                }
              />
            </div>
          </div>
        )}
        {!screenShareAvl && (
          <button className="screenShareBtn" onClick={handleScreeShareNoticationEvent}>
            <div className="screenShareBtn_flex">
              <img className="shareScreenIcon" src={shareScreenIcon} alt="" />
              <span>Share screen</span>
            </div>
          </button>
        )}
        {screenShareAvl &&
          visibleControls.screenShare &&
          (!!(window as any).electronAPI ? (
            <ScreenShareElectron />
          ) : (
            <TrackToggle
              source={Track.Source.ScreenShare}
              captureOptions={{ audio: true, selfBrowserSurface: "include" }}
              showIcon={showIcon}
              onChange={onScreenShareChange}
            >
              {showText &&
                (isScreenShareEnabled ? "Stop screen share" : "Share screen")}
            </TrackToggle>
          ))}
        {(isFeatureConfigured?.features.sessionRecording &&( visibleControls.camera || visibleControls.microphone)) && 
          (<button disabled={sessionRecordingDisabled} className="sessionRecordBtn" onClick={handleSessionRecording}>
            <div className="sessionRecordBtn_flex">
              <img className="sessionRecordIcon" src={sessionRecordingIcon} alt="" />
              <span>{sessionRecordingLabel}</span>
            </div>
          </button>) }
          

        {visibleControls.chat && (
          <ChatToggle>
            {showIcon && <ChatIcon />}
            {showText && "Chat"}
          </ChatToggle>
        )}
        {visibleControls.leave && (
          <>
          {!adminLeaveOption ? <DisconnectButton onClick={onLeaveSession}>
            {showIcon && <LeaveIcon />}
            {showText && "Leave"}
          </DisconnectButton> :  <LeaveButtonWithDropdown
              onLeaveSession={onLeaveSession}
              onEndForAll={onEndForAll}
              showIcon={true}
              showText={true} />}

         
          </>
        )}
      </div>
    </>
  );
}